import React, { useMemo } from "react";
import { Box, Button, IconButton, Tooltip, Typography } from "@mui/material";
import HistoryIcon from "@mui/icons-material/History";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Inventory } from "@mui/icons-material";

/**
 * Compact order row (order number + PO) with "Track Order" and "View in Order History" actions.
 *
 * @param {Object} props
 * @param {Object} props.order Order row; search results use `product_id`/`name` for id/number.
 * @param {string} [props.buttonText="Track Order"] Label of the track action.
 * @param {Function} [props.onTrack] Called instead of navigating to /track-order when provided.
 * @param {boolean} [props.showTrackButton=true] Hide the track action; search results turn it
 *   off because Track Order is not an explicit page in the navigation.
 * @returns {JSX.Element}
 */
const OrderListItem = ({ order, buttonText = 'Track Order', onTrack, showTrackButton = true }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Derive PO number from available order fields (prefer order_data.po_number)
  const poNumber = useMemo(() => {
    try {
      if (!order) return "—";
      // Direct property
      if (order.order_po) return order.order_po;
      // Nested order_data structures
      if (order.order_data) {
        const od = typeof order.order_data === 'string' ? JSON.parse(order.order_data) : order.order_data;
        if (od?.po_number) return od.po_number;
        if (od?.details?.po_number) return od.details.po_number;
        if (od?.payment_details?.po_number) return od.payment_details.po_number;
      }
      if (order.details?.po_number) return order.details.po_number;
      // Fallbacks
      if (order.sub_name) return order.sub_name;
    } catch (_) { }
    return "—";
  }, [order]);

  const handleVisit = () => {
    const params = new URLSearchParams();
    // order from search results may use product_id for order_id and name for order_number
    const orderId = order?.order_id ?? order?.product_id;
    const orderNumber = order?.order_number ?? order?.name;
    if (orderId) params.set('open_id', orderId);
    if (orderNumber) params.set('open_number', orderNumber);
    // If onTrack is provided (e.g., Track Order page), call it instead of navigating
    if (typeof onTrack === 'function') {
      onTrack({ orderId, orderNumber, order });
    } else {
      navigate(`/track-order${params.toString() ? `?${params.toString()}` : ''}`);
    }
  };

  const handleHistoryVisit = () => {
    const params = new URLSearchParams();
    const orderId = order?.order_id ?? order?.product_id;
    const orderNumber = order?.order_number ?? order?.name;
    if (orderId) params.set('open_id', orderId);
    if (orderNumber) params.set('open_number', orderNumber);
    navigate(`/order-history${params.toString() ? `?${params.toString()}` : ''}`);
  };
  return (
    <Box
      sx={{
        width: "100%",
        maxHeight: "75px",
        borderRadius: 2,
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        backgroundColor: "#fff",
        boxShadow: "0 0 6px 1px rgba(0,0,0,0.1)",
        p: 2,
        columnGap: 2,
        margin: "7px 0",
      }}
    >
      {/* flex:1 + minWidth:0 so both lines run up to the buttons before they ellipse. */}
      <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0 }}>
        <Tooltip title={order.name || ""}>
          <Typography variant="body1" sx={{ fontWeight: "bold", fontSize: "12px", whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', width: '100%' }}>
            {order.name}
          </Typography>
        </Tooltip>
        <Tooltip title={`PO: ${poNumber ?? ""}`}>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: "11px", whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', width: '100%' }}>
            PO: {poNumber}
          </Typography>
        </Tooltip>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', columnGap: 0, flexShrink: 0 }}>
        {showTrackButton && (
          <Button size="small" endIcon={<HistoryIcon />} onClick={handleVisit}>
            {buttonText}
          </Button>
        )}

        <Button size="small" endIcon={<Inventory />} onClick={handleHistoryVisit}>
          {t('LABELS.VIEW_IN_ORDER_HISTORY')}
        </Button>
      </Box>
    </Box>
  );
};

export default OrderListItem;
